import React from "react";

export interface SettingsListColumnMeasurement {
  key: string;
  title: number;
  content: number;
  action: boolean;
}

export interface SettingsListGroupMeasurement {
  schema: string;
  columns: SettingsListColumnMeasurement[];
  fullActions: number;
  iconActions: number;
  availableWidth: number;
}

export function createSettingsListColumnGroup() {
  const members = new Map<string, SettingsListGroupMeasurement>();
  const listeners = new Set<() => void>();
  let snapshot: ReadonlyMap<string, SettingsListGroupMeasurement> = new Map();
  const publish = () => {
    const next = new Map<string, SettingsListGroupMeasurement>();
    for (const member of members.values()) {
      const current = next.get(member.schema);
      if (!current) {
        next.set(member.schema, { ...member, columns: member.columns.map(column => ({ ...column })) });
        continue;
      }
      current.columns.forEach((column, index) => {
        column.title = Math.max(column.title, member.columns[index].title);
        column.content = Math.max(column.content, member.columns[index].content);
      });
      current.fullActions = Math.max(current.fullActions, member.fullActions);
      current.iconActions = Math.max(current.iconActions, member.iconActions);
      current.availableWidth = Math.min(current.availableWidth, member.availableWidth);
    }
    snapshot = next;
    listeners.forEach(listener => listener());
  };
  return {
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    getSnapshot: () => snapshot,
    update(id: string, value: SettingsListGroupMeasurement) {
      if (JSON.stringify(members.get(id)) === JSON.stringify(value)) return;
      members.set(id, value);
      publish();
    },
    remove(id: string) { if (members.delete(id)) publish(); },
  };
}

export const SettingsListColumnGroupContext = React.createContext<ReturnType<typeof createSettingsListColumnGroup> | null>(null);
const emptySnapshot: ReadonlyMap<string, SettingsListGroupMeasurement> = new Map();
const noSubscribe = () => () => {};
const getEmptySnapshot = () => emptySnapshot;

/** Measurements come from the list's DOM, never from an already allocated group track. */
export function useSettingsListColumnGroup(measurement: SettingsListGroupMeasurement) {
  const group = React.useContext(SettingsListColumnGroupContext);
  const id = React.useId();
  const snapshot = React.useSyncExternalStore(group?.subscribe ?? noSubscribe, group?.getSnapshot ?? getEmptySnapshot, getEmptySnapshot);
  const serialized = JSON.stringify(measurement);
  React.useLayoutEffect(() => {
    if (group && measurement.availableWidth > 0) group.update(id, JSON.parse(serialized) as SettingsListGroupMeasurement);
  }, [group, id, serialized, measurement.availableWidth]);
  React.useLayoutEffect(() => () => group?.remove(id), [group, id]);
  return group ? snapshot.get(measurement.schema) : undefined;
}
